/**
 * 运营支票的英文用途：从付款原因生成建议，财务手填始终优先。
 * 不自动保存/批准；原因变更或手动编辑会取消旧请求，防止慢响应覆盖财务输入。
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { Button } from "../ui/button";

export default function OperatingCheckMemo({ zh, note, category, branch, value, initialValue, onChange, onBusy, disabled }) {
  const { api } = useApp();
  const [busy, setBusy] = useState(false), [warning, setWarning] = useState("");
  const manual = useRef(Boolean(initialValue ?? value)), request = useRef(null), sequence = useRef(0);
  const latest = useRef({});
  latest.current = { api, note, category, branch, onChange, onBusy, zh, disabled };
  const markBusy = useCallback((next) => { setBusy(next); latest.current.onBusy?.(next); }, []);
  const cancel = useCallback(() => { sequence.current += 1; request.current?.abort(); }, []);
  const generate = useCallback(async () => {
    const current = latest.current;
    if (!current.note?.trim() || !current.branch || current.disabled) { markBusy(false); return; }
    cancel(); const id = sequence.current;
    request.current = new AbortController(); markBusy(true); setWarning("");
    try {
      const { data } = await current.api.post("/finance/operating-requests/memo-suggestion", {
        note: current.note, opCategory: current.category, branch: current.branch,
      }, { signal: request.current.signal, timeout: 20000 });
      if (id !== sequence.current) return;
      if (data.memo) { manual.current = false; latest.current.onChange(data.memo); }
      setWarning(data.warning || "");
    } catch (e) {
      if (id === sequence.current) setWarning(current.zh ? "暂时无法生成，请手动填写英文用途。" : "Unable to generate. Enter the English purpose manually.");
    } finally { if (id === sequence.current) markBusy(false); }
  }, [cancel, markBusy]);
  useEffect(() => {
    cancel(); markBusy(false);
    // 已保存或手填的用途不随原因自动重写；财务可以明确点击“重新生成”。
    if (manual.current || !note?.trim() || !branch) return;
    markBusy(true);
    const timer = setTimeout(() => { if (!manual.current) generate(); }, 800);
    return () => { clearTimeout(timer); cancel(); };
  }, [note, category, branch, generate, cancel, markBusy]);
  useEffect(() => () => { cancel(); latest.current.onBusy?.(false); }, [cancel]);
  return <div className="space-y-2" data-testid="op-memo-field">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <label htmlFor="operating-check-memo" className="text-sm font-medium text-slate-600">{zh ? "支票用途备注 MEMO" : "Check purpose / MEMO"}</label>
      <Button type="button" variant="ghost" size="sm" disabled={disabled || busy || !note?.trim() || !branch} onClick={generate}>
        {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}{busy ? (zh ? "正在生成…" : "Generating…") : (zh ? "根据付款原因生成" : "Generate from reason")}
      </Button>
    </div>
    <input id="operating-check-memo" data-testid="op-memo-input" maxLength={80} value={value || ""} disabled={disabled}
      className="w-full min-h-11 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300 disabled:bg-slate-50"
      placeholder={zh ? "例如 Office supplies - October 2026" : "e.g. Office supplies - October 2026"}
      onChange={(e) => { manual.current = true; cancel(); markBusy(false); setWarning(""); onChange(e.target.value); }} />
    <div className="flex justify-between gap-2 text-xs text-slate-500"><span>{zh ? "英文用途，可手动修改；保存后印在支票上。" : "English purpose. Editable; printed after saving."}</span><span className="shrink-0 tabular-nums">{(value || "").length}/80</span></div>
    {warning && <p role="status" className="text-xs text-amber-800">{warning}</p>}
  </div>;
}
